import { FormField, Input, Select } from "@/components/ui";
import type { HomeBlockType } from "@/lib/api";

export interface BlockMetadataFieldsProps {
  type: HomeBlockType;
  metadata: Record<string, unknown>;
  onChange: (metadata: Record<string, unknown>) => void;
  disabled?: boolean;
}

function textValue(metadata: Record<string, unknown>, key: string): string {
  const value = metadata[key];
  return typeof value === "string" ? value : "";
}

/**
 * Type-specific metadata inputs per docs/MOBILE_CONFIG_CONTRACT.md "Metadata
 * validation". The backend is authoritative on validation - this just
 * collects the fields into the right shape.
 */
export function BlockMetadataFields({ type, metadata, onChange, disabled = false }: BlockMetadataFieldsProps) {
  function set(key: string, value: string) {
    onChange({ ...metadata, [key]: value || undefined });
  }

  switch (type) {
    case "daily_verse":
      return (
        <FormField label="Id del versiculo diario (opcional)" hint="Referencia a un daily_verses.id concreto.">
          {(field) => (
            <Input {...field} disabled={disabled} value={textValue(metadata, "dailyVerseId")} onChange={(e) => set("dailyVerseId", e.target.value)} />
          )}
        </FormField>
      );
    case "daily_reflection":
      return (
        <FormField label="Id de la reflexion diaria (opcional)" hint="Referencia a un daily_reflections.id concreto.">
          {(field) => (
            <Input {...field} disabled={disabled} value={textValue(metadata, "dailyReflectionId")} onChange={(e) => set("dailyReflectionId", e.target.value)} />
          )}
        </FormField>
      );
    case "event":
      return (
        <div className="space-y-3">
          <FormField label="Id del evento (opcional)">
            {(field) => <Input {...field} disabled={disabled} value={textValue(metadata, "eventId")} onChange={(e) => set("eventId", e.target.value)} />}
          </FormField>
          <FormField label="Ubicacion (opcional)">
            {(field) => <Input {...field} disabled={disabled} value={textValue(metadata, "location")} onChange={(e) => set("location", e.target.value)} />}
          </FormField>
          <FormField label="URL externa (opcional)">
            {(field) => (
              <Input {...field} disabled={disabled} type="url" value={textValue(metadata, "externalUrl")} onChange={(e) => set("externalUrl", e.target.value)} />
            )}
          </FormField>
        </div>
      );
    case "announcement":
      return (
        <div className="space-y-3">
          <FormField label="Severidad (opcional)">
            {(field) => (
              <Select {...field} disabled={disabled} value={textValue(metadata, "severity")} onChange={(e) => set("severity", e.target.value)}>
                <option value="">Sin especificar</option>
                <option value="info">Informativa</option>
                <option value="warning">Aviso</option>
                <option value="critical">Critica</option>
              </Select>
            )}
          </FormField>
          <FormField label="Categoria (opcional)">
            {(field) => <Input {...field} disabled={disabled} value={textValue(metadata, "category")} onChange={(e) => set("category", e.target.value)} />}
          </FormField>
        </div>
      );
    case "first_steps":
      return (
        <FormField label="Ruta o id de paso (opcional)">
          {(field) => <Input {...field} disabled={disabled} value={textValue(metadata, "route")} onChange={(e) => set("route", e.target.value)} />}
        </FormField>
      );
    case "prepare_visit":
      return (
        <FormField label="Id de ubicacion o ruta (opcional)">
          {(field) => <Input {...field} disabled={disabled} value={textValue(metadata, "locationId")} onChange={(e) => set("locationId", e.target.value)} />}
        </FormField>
      );
    case "custom_banner":
      return (
        <div className="space-y-3">
          <FormField label="Variante de diseno (opcional)">
            {(field) => <Input {...field} disabled={disabled} value={textValue(metadata, "layoutVariant")} onChange={(e) => set("layoutVariant", e.target.value)} />}
          </FormField>
          <FormField label="Clave de seguimiento (opcional)">
            {(field) => <Input {...field} disabled={disabled} value={textValue(metadata, "trackingKey")} onChange={(e) => set("trackingKey", e.target.value)} />}
          </FormField>
        </div>
      );
    default:
      return null;
  }
}
